/**
 * A custom hook for staging a product.
 *
 * This hook retrieves the session ID, vendor URL, and timestamp from the SessionContext.
 * It also provides a stagingResult, error, and stageProduct function to interact with the staging API.
 *
 * @returns {Object} An object containing the stagingResult, error, and stageProduct function.
 * @property {Object} stagingResult - The result of the staging API call.
 * @property {Object} error - The error object if the staging API call fails.
 * @property {Function} stageProduct - A function to stage a product with the given image hash and product ID.
 * @param {string} image_hash - The image hash of the product.
 * @param {string} product_id - The ID of the product.
 */

import { useState } from "react";
import { useSession } from "../utils/SessionContext";
import api from "../api/api";
import unixTimestamp from "../utils/unixTimestamp";

const useInteractiveSofaStaging = () => {
  const { session_id, vendorUrl } = useSession();
  const [error, setError] = useState(null);
  
  const stageInteractiveSofa = async (
    x, y, yaw, width, height, room_image_hash, product_id, position_source,
    stagingResult, setStagingResult, setProductStagingLoading, setIsInteractingNow) => {
    
    const url_start = `/stage_interactive_sofa`;
    const params_start = {
        session_id,
        vendor_url: vendorUrl,
        product_id,
        x,
        y, 
        width, 
        height,
        yaw,
        room_image_hash,
        position_source
    };

    const url_join = `/product_staging`;
    const params_join = {
      session_id,
      vendor_url: vendorUrl,
      product_id,
      image_hash: room_image_hash,
      activity_stage: "join",
      task_id: "<placeholder>", 
      timestamp: unixTimestamp(),
      geometry_unique_str: null,
    };

    const original_image = stagingResult

    try {
      setProductStagingLoading(true);

      const res_start = await api.post(url_start, null, {
        params: params_start,
        timeout: 200000, // Timeout 200 seconds
      });
    
      setStagingResult(res_start.data);
      
      params_join.geometry_unique_str = res_start.data.geometry_unique_str; 
      
      if (res_start.data.need_join === true) {
          params_join.task_id = res_start.data.task_id;
          const res_join = await api.post(url_join, null, {
            params: params_join,
            timeout: 200000, // Timeout 200 seconds
          });
          setStagingResult(res_join.data);
      }
    } catch (err) {
      setError(err.response ? err.response.data : { message: err.message });
      setStagingResult(original_image);
    } finally {
        setProductStagingLoading(false);
        setIsInteractingNow(false)
    }    
  };

  return { stageInteractiveSofa }; 

};

export default useInteractiveSofaStaging;
